import { V1AssetRequest } from '@blk/portfolio-security-server/asset';
import { IdentifiersMap } from '@ddt-web/domain';
import { WweSecurityDetailsParams } from '@ddt-web/wealth-web';
import * as isNil from 'lodash/isNil';

import { AssetResolveRequest, IDENTIFIER_TYPE, SecurityIdentifiersMapRequest } from './asset-resolve-request.model';

// Map WWE securities API request to PAS Asset Resolve API request
export const mapSecurityRequestToAssetResolveRequest = (wweSecurityDetailsParams: WweSecurityDetailsParams, isAmbiguousResolve: boolean = false) => {
	return {
		assetRequests: wweSecurityDetailsParams?.body?.securities
			? wweSecurityDetailsParams?.body?.securities?.map(security => mapWweSecurityIdentifiersToAssetIdentifiers(security, isAmbiguousResolve)) : [],
		rc: wweSecurityDetailsParams.rc,
		asOfDate: wweSecurityDetailsParams.date ?? null
		// rc: wweSecurityDetailsParams.rc.includes('tax') ? null : wweSecurityDetailsParams.rc,
		// asOfDate: wweSecurityDetailsParams.rc.includes('tax') ? '2023-05-26' : null,
		// pkg: wweSecurityDetailsParams.rc.includes('tax') ? 'AC_TAX' : null
	} as AssetResolveRequest;
};

export const mapWweSecurityIdentifiersToAssetIdentifiers = (securityIdentifiersMapRequest: SecurityIdentifiersMapRequest, isAmbiguousResolve: boolean = false) => {
	return {
		requestId: securityIdentifiersMapRequest.id,
		identifiers: mapSecurityIdentifiersToAssetIdentifiers(securityIdentifiersMapRequest.identifiers, isAmbiguousResolve)
	} as V1AssetRequest;
};

/**
 * Maps key value identifiers to the PAS identifiers format
 * @param identifiers identifiers map in a key value format 
 * @param useAladdinId 
 * @returns the identifiers map in the identifierType/identifierValue object format
 */
export const mapIdentifiersToSecurityIdentifiersMap = (identifiers: { [key: string]: string }) => {
	return mapSecurityIdentifiersToAssetIdentifiers(identifiers);
};


export const mapSecurityIdentifiersToAssetIdentifiers = (identifiersMap: IdentifiersMap, isAmbiguousResolve?: boolean) => {

	let identifiersArray = [
		{
			identifierType: IDENTIFIER_TYPE['morningstarSecId'],
			identifierValue: identifiersMap.morningstarSecId
		},
		{
			identifierType: IDENTIFIER_TYPE['clientId'],
			identifierValue: identifiersMap.clientId ?? identifiersMap.globalId
		},
		{
			identifierType: IDENTIFIER_TYPE['sedol'],
			identifierValue: identifiersMap.sedol
		},
		{
			identifierType: IDENTIFIER_TYPE['ticker'],
			identifierValue: identifiersMap.ticker
		},
		{
			identifierType: IDENTIFIER_TYPE['modelId'],
			identifierValue: identifiersMap.modelId
		}
	];

	if (isAmbiguousResolve) { // for import holdings flow, identifier mapping is straightforward
		identifiersArray = [
			...identifiersArray,
			{
				identifierType: IDENTIFIER_TYPE['bcusip'],
				identifierValue: identifiersMap.aladdinId
			},
			{
				identifierType: IDENTIFIER_TYPE['cusip'],
				identifierValue: identifiersMap.cusip || identifiersMap.publicCusip
			},
			{
				identifierType: IDENTIFIER_TYPE['isin'],
				identifierValue: identifiersMap.isin
			}
		];
	} else { // for asset resolve some more custom logic is required
		identifiersArray = [
			...identifiersArray,
			{
				identifierType: IDENTIFIER_TYPE['bcusip'],
				identifierValue: identifiersMap.aladdinId || identifiersMap.cusip // the cusip fallback is technically incorrect but there's a bug in AWSecurity where not all securities have mapping available for publicCusip. ClientId is resolved first so this shouldn't be an issue
			},
			{
				identifierType: IDENTIFIER_TYPE['cusip'],
				// Dont know if we ever use publicCusip but its present as a fallback
				identifierValue: identifiersMap.cusip || identifiersMap.publicCusip
			}
		];

		if (checkIfOnlyCusipAndIsinAvailable(identifiersMap)) {
			// add isin only in this case
			identifiersArray = [
				...identifiersArray,
				{
					identifierType: IDENTIFIER_TYPE['isin'],
					identifierValue: identifiersMap.isin
				}
			];
		}
	}
	return identifiersArray.filter(item => !!item.identifierValue);
};

/**
 * Check if only the cusip and isin are available. Needed for the case where we have only have the WDS SECURITY_ID available (TICKER@CUSIP@ISIN) 
 */
export const checkIfOnlyCusipAndIsinAvailable = (identifiersMap: IdentifiersMap) => {
	const availableIdentifiers: any[] = Object.keys(identifiersMap)?.filter((key) => !isNil(identifiersMap[key]));
	return availableIdentifiers?.length === 2 && availableIdentifiers?.includes('cusip') && availableIdentifiers?.includes('isin');
};
